import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/ui';
import { Combobox } from '@/components/ui/combobox';
import { IpRangeInput } from './ip-range-input';
import { collectIpRanges } from '@/lib/ip/parse-ip-range';
import { getCountryOptions, countryDisplayName } from '@/lib/data/iso-3166';
import type {
  CreateMnoIpRangeInput,
  MnoIpRangeEntry,
  MnoIpRangeStatus,
  MnoOperator,
} from '@/types/mno-ip-range';

interface MnoIpRangeFormProps {
  mode: 'create' | 'edit';
  operators: MnoOperator[];
  initial?: MnoIpRangeEntry;
  submitting?: boolean;
  /** Server-Validierungsfehler (400/422) — feldnah/formularnah anzeigen. */
  submitError?: string | null;
  onSubmit: (input: CreateMnoIpRangeInput) => void;
  onCancel: () => void;
}

export function MnoIpRangeForm({
  mode,
  operators,
  initial,
  submitting,
  submitError,
  onSubmit,
  onCancel,
}: MnoIpRangeFormProps) {
  const { t, i18n } = useTranslation('mno-ip-ranges');
  // Composite Key (operatorId + country) ist im Edit read-only — der Contract
  // kennt PUT nur auf /{operatorId}/{countryCode}, ein Schluesselwechsel ist
  // fachlich ein Loeschen+Neuanlegen (Spec 326 §2).
  const isEdit = mode === 'edit';

  const [operatorId, setOperatorId] = useState(initial?.operatorId ?? '');
  const [country, setCountry] = useState(initial?.country.code ?? '');
  const [rawRanges, setRawRanges] = useState<string[]>(
    initial?.ipRanges && initial.ipRanges.length > 0
      ? initial.ipRanges.map((r) => r.cidr)
      : [''],
  );
  const [label, setLabel] = useState(initial?.label ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [status, setStatus] = useState<MnoIpRangeStatus>(initial?.status ?? 'active');
  const [showErrors, setShowErrors] = useState(false);

  const countryOptions = useMemo(
    () =>
      getCountryOptions(i18n.language).map((c) => ({
        value: c.code,
        label: `${c.name} (${c.code})`,
      })),
    [i18n.language],
  );

  // Operator-Auswahl haengt am Land (#322): nur Operatoren des gewaehlten Landes.
  const operatorsForCountry = useMemo(
    () => operators.filter((op) => op.country === country),
    [operators, country],
  );

  const handleCountryChange = (next: string) => {
    setCountry(next);
    // Operator zuruecksetzen, falls er nicht mehr zum gewaehlten Land passt.
    if (operatorId && !operators.some((op) => op.operatorId === operatorId && op.country === next)) {
      setOperatorId('');
    }
  };

  const collected = useMemo(() => collectIpRanges(rawRanges), [rawRanges]);

  // Gemischte v4/v6 sind seit Spec 326 erlaubt — kein mixedVersions-Fehler mehr.
  const rangeError = !collected.hasValidRange
    ? t('form.error.rangesRequired')
    : Object.keys(collected.errors).length > 0
      ? t('form.error.rangesInvalid')
      : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (!operatorId || !country || rangeError || !collected.hasValidRange) return;
    onSubmit({
      operatorId,
      country: { code: country, name: countryDisplayName(country, i18n.language) },
      cidrs: collected.cidrs,
      label: label.trim() || undefined,
      description: description.trim() || undefined,
      status,
    });
  };

  const operatorName =
    operators.find((op) => op.operatorId === operatorId)?.name ?? operatorId;

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      {isEdit ? (
        // Read-only Composite Key im Edit-Modus.
        <div className="grid gap-1 rounded-md border bg-muted/30 p-3 text-sm">
          <span className="text-muted-foreground">{t('form.keyReadonly')}</span>
          <span className="font-medium">
            {initial?.country.name ?? country} ({country}) · {operatorName}
          </span>
        </div>
      ) : (
        <>
          <div className="grid gap-2">
            <Label htmlFor="mnoip-country">{t('form.country')}</Label>
            <Combobox
              id="mnoip-country"
              options={countryOptions}
              value={country}
              onChange={handleCountryChange}
              placeholder={t('form.countryPlaceholder')}
              searchPlaceholder={t('form.countrySearch')}
              error={showErrors && !country}
            />
            {showErrors && !country && (
              <p className="text-sm text-destructive">{t('form.error.countryRequired')}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="mnoip-operator">{t('form.operator')}</Label>
            <Select value={operatorId} onValueChange={setOperatorId} disabled={!country}>
              <SelectTrigger id="mnoip-operator" aria-invalid={showErrors && !!country && !operatorId}>
                <SelectValue placeholder={t('form.operatorPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {operatorsForCountry.map((op) => (
                  <SelectItem key={op.operatorId} value={op.operatorId}>
                    {op.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!country ? (
              <p className="text-sm text-muted-foreground">{t('form.operatorSelectCountryFirst')}</p>
            ) : operatorsForCountry.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('form.noOperatorsForCountry')}</p>
            ) : (
              showErrors &&
              !operatorId && (
                <p className="text-sm text-destructive">{t('form.error.operatorRequired')}</p>
              )
            )}
          </div>
        </>
      )}

      <div className="grid gap-2">
        <Label>{t('form.ranges')}</Label>
        <p className="text-sm text-muted-foreground">{t('form.rangesHint')}</p>
        <IpRangeInput value={rawRanges} onChange={setRawRanges} disabled={submitting} />
        {showErrors && rangeError && <p className="text-sm text-destructive">{rangeError}</p>}
      </div>

      <div className="grid gap-2">
        <Label htmlFor="mnoip-label">{t('form.label')}</Label>
        <Input
          id="mnoip-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('form.labelPlaceholder')}
          maxLength={200}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="mnoip-description">{t('form.description')}</Label>
        <Textarea
          id="mnoip-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('form.descriptionPlaceholder')}
          maxLength={500}
          rows={3}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="mnoip-status">{t('form.status')}</Label>
        <Select value={status} onValueChange={(v) => setStatus(v as MnoIpRangeStatus)}>
          <SelectTrigger id="mnoip-status" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">{t('status.active')}</SelectItem>
            <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {submitError && (
        <p role="alert" className="text-sm font-medium text-destructive">
          {submitError}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={submitting}>
          {mode === 'create' ? t('form.submitCreate') : t('form.submitSave')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          {t('common:button.cancel')}
        </Button>
      </div>
    </form>
  );
}
